import type { JSX } from "react";

import type {
  CalculatorLevel,
  FormColumn as ServiceFormColumn,
} from "../../services/calculatorService";
import { AddRowTable, type AddRowTableConfig } from "./AddRowTable";
import type { ExtendedFormRow } from "./formTypes";
import {
  MIN_PROCUREMENT_ITEMS,
  PROCUREMENT_COLUMNS,
  PROCUREMENT_HEADER_LABELS,
  PROCUREMENT_TOOLTIPS,
} from "./procurementCatalogue";

/**
 * Procurement (Wet Lab Silver) table — issue #305.
 *
 * The table itself is `AddRowTable`, shared with the gold assessments. This
 * supplies Procurement's copy and geometry and keeps the prop surface the call
 * site and the stories already use.
 *
 * One add-row table: each row names a consumable through three cascading
 * dropdowns, then captures order quantities and a reuse option.
 */

/**
 * Twelfths. The eight columns plus Remove must total 12, or `table-fixed`
 * scales everything down until labels break mid-word.
 *
 * The three dropdowns carry the long option text ("Extra-Large Petri dish",
 * "Yes - recycled content") and get the width; the numeric fields need room for
 * four digits and no more.
 */
const COLUMN_WIDTH: Record<string, string> = {
  [PROCUREMENT_COLUMNS.itemCategory]: "w-2/12",
  [PROCUREMENT_COLUMNS.itemType]: "w-2/12",
  [PROCUREMENT_COLUMNS.sizeVolume]: "w-1/12",
  [PROCUREMENT_COLUMNS.catalogueNumber]: "w-1/12",
  [PROCUREMENT_COLUMNS.unitsPerPack]: "w-1/12",
  [PROCUREMENT_COLUMNS.packsPerOrder]: "w-1/12",
  [PROCUREMENT_COLUMNS.monthsBetweenOrders]: "w-1/12",
  [PROCUREMENT_COLUMNS.reuseOrAlternatives]: "w-2/12",
};

interface TableProps {
  level: CalculatorLevel;
  columns: ServiceFormColumn[];
  rows: ExtendedFormRow[];
  renderField: (row: ExtendedFormRow, column: ServiceFormColumn) => JSX.Element;
  /** Baseline lets rows be added and removed; savings inherits the baseline's
   * item set and must not change it. */
  allowRemoveRows: boolean;
  onRemoveRow: (rowId: string) => void;
  locked: boolean;
  /** Rows with every required field filled. Drives the progress line; the
   * submit gate itself lives in Calculator. */
  completeCount?: number;
  /** Savings has no minimum: the baseline already enforced it. */
  showItemCount?: boolean;
  /** A row's current values by column id, for building row headings. */
  valuesOf: (rowId: string) => Record<string, string>;
}

export const ProcurementTable = ({
  completeCount = 0,
  showItemCount = true,
  ...props
}: TableProps): JSX.Element => {
  const config: AddRowTableConfig = {
    widths: COLUMN_WIDTH,
    headerLabels: PROCUREMENT_HEADER_LABELS,
    tooltipFor: (column) => PROCUREMENT_TOOLTIPS[column.label],
    // Item Type and Size name the consumable; Item Category is implied by them.
    headingColumns: [
      PROCUREMENT_COLUMNS.itemType,
      PROCUREMENT_COLUMNS.sizeVolume,
    ],
    emptyNote: (
      <>
        No items yet. Use <strong>Add Row</strong> below to record each
        single-use item your lab orders.
      </>
    ),
    footerNote: showItemCount ? (
      <>
        {completeCount} of {MIN_PROCUREMENT_ITEMS} items complete. Each item can
        only be added once, and you can mix categories freely.
      </>
    ) : undefined,
  };

  return <AddRowTable {...props} config={config} />;
};
